import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@/hooks/use-user';
import {
  getModifierName,
  hasCriticalErrors,
  isProductModified,
} from '@/lib/utils';
import { displayModeState } from '@/states/display-mode.state';
import { productsState } from '@/states/products.state';
import { DisplayMode } from '@/types/display-mode';
import { useAtom, useAtomValue } from 'jotai';

import { ActorRole } from '../lib/graphql/generated/graphql';
import { editionModeState } from '../states/edition-mode.state';

export function useDisplayModeData() {
  const { t } = useTranslation();
  const user = useUser();

  const [displayMode] = useAtom(displayModeState);
  const products = useAtomValue(productsState);
  const editionMode = useAtomValue(editionModeState);

  const counts = useMemo(() => {
    const selectedProducts = products.filter((p) => p.activeVersion.isSelected);
    const regroupedProductsCount = new Set(
      selectedProducts.map((p) => p.activeVersion.productGroupKey)
    ).size;
    const variantsCount = selectedProducts.length;

    const excludedCount = products.filter(
      (p) => !p.activeVersion.isSelected
    ).length;

    const errorsCount = products.filter(
      (product) =>
        product.activeVersion.isSelected &&
        hasCriticalErrors(product.activeVersion.validationErrors)
    ).length;

    const modifiedCount = products.filter((p) =>
      user?.role ? isProductModified(p, user.role, editionMode) : false
    ).length;

    const modifiedByChooseCount = products.filter((p) =>
      isProductModified(p, ActorRole.Brand, editionMode)
    ).length;

    const selectedCount =
      regroupedProductsCount === variantsCount
        ? regroupedProductsCount
        : t('components.other.included_badge_format', {
            productsCount: regroupedProductsCount,
            variantsCount: variantsCount,
          });

    return {
      regroupedProductsCount,
      variantsCount,
      excludedCount,
      errorsCount,
      modifiedCount,
      modifiedByChooseCount,
      selectedCount,
    };
  }, [products, user?.role, editionMode, t]);

  const getDisplayModeLabel = useCallback(
    (mode: DisplayMode): string => {
      switch (mode) {
        case DisplayMode.SELECTED:
          return t('components.other.included');
        case DisplayMode.DESELECTED:
          return t('components.other.excluded');
        case DisplayMode.ERROR:
          return t('components.other.to_be_completed');
        case DisplayMode.MODIFIED:
          return t('components.other.modified_by', {
            author: user?.role
              ? getModifierName(user.role, t('general.brand_role'))
              : '...',
          });
        case DisplayMode.MODIFIED_BY_CHOOSE:
          return t('components.other.modified_by', {
            author: t('components.other.me'),
          });
        default:
          return '';
      }
    },
    [t, user?.role]
  );

  const getDisplayModeCount = useCallback(
    (mode: DisplayMode): string | number => {
      switch (mode) {
        case DisplayMode.SELECTED:
          return counts.selectedCount;
        case DisplayMode.DESELECTED:
          return counts.excludedCount;
        case DisplayMode.ERROR:
          return counts.errorsCount;
        case DisplayMode.MODIFIED:
          return counts.modifiedCount;
        case DisplayMode.MODIFIED_BY_CHOOSE:
          return counts.modifiedByChooseCount;
        default:
          return 0;
      }
    },
    [counts]
  );

  return {
    displayMode,
    counts,
    getDisplayModeLabel,
    getDisplayModeCount,
  };
}
